import React from "react";
import { ArrowRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/routing";
import { useAboutData } from "@/contexts/HomeDataContext";
import { useTranslations } from 'next-intl';

export const AboutSection: React.FC = () => {
  const data = useAboutData();
  const t = useTranslations('About');

  return (
    <section className="relative w-full py-8 text-center md:text-left rtl:md:text-right md:py-20 lg:py-24 bg-x-100 overflow-hidden">
      <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          
          {/* Image Section */}
          <div className="order-2 lg:order-1 rtl:lg:order-2">
            <div className="relative translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
              <div className="relative w-full max-w-[500px] md:max-w-[600px] lg:max-w-[653px] mx-auto lg:mx-0 rtl:lg:mx-0 min-h-[300px]">
                {/* Under Element SVG - Behind the main image */}
                <div className="absolute inset-0 w-full h-full z-0">
                  <img
                    src={data.images.background.src}
                    alt={data.images.background.alt}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Main Image - On top */}
                <img
                  src={data.images.main.src}
                  alt={data.images.main.alt}
                  className="relative z-20 w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="order-1 lg:order-2 rtl:lg:order-1 space-y-6 md:space-y-8">
            
            {/* Tagline and Heading */}
            <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
              <div className="bg-brand-gradient-text font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)] mb-3 md:mb-4">
                {t('tagline')}
              </div>
              
              <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
                {t('heading')}
              </h2>
            </div>

            {/* Description */}
            <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
              <p className="font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-sm md:text-base lg:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-relaxed lg:leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                {t('description')}
              </p>
            </div>

            {/* Bullet Points */}
            <div className="space-y-4 md:space-y-5 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms]">
              {Array.from({ length: 3 }, (_, index) => (
                <div key={index} className="flex items-start gap-3 md:gap-4">
                  {/* Custom Gradient Checkmark SVG */}
                  <div className="flex-shrink-0 mt-0.5">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 md:w-6 md:h-6">
                      <path d="M12 0C13.0938 0 14 0.90625 14 2V12C14 13.125 13.0938 14 12 14H2C0.875 14 0 13.125 0 12V2C0 0.90625 0.875 0 2 0H12ZM10.5938 5.625C10.9375 5.28125 10.9375 4.75 10.5938 4.40625C10.25 4.0625 9.71875 4.0625 9.375 4.40625L6 7.78125L4.59375 6.40625C4.25 6.0625 3.71875 6.0625 3.375 6.40625C3.03125 6.75 3.03125 7.28125 3.375 7.625L5.375 9.625C5.71875 9.96875 6.25 9.96875 6.59375 9.625L10.5938 5.625Z" fill="url(#paint0_linear_3688_22)"/>
                      <defs>
                        <linearGradient id="paint0_linear_3688_22" x1="-0.0886736" y1="4.7285" x2="14.1546" y2="4.94898" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#08264C"/>
                          <stop offset="1" stopColor="#3F74EF"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <span className="font-heading-h4 font-[number:var(--heading-h4-font-weight)] text-x-900 text-sm md:text-base lg:text-[length:var(--heading-h4-font-size)] tracking-[var(--heading-h4-letter-spacing)] leading-relaxed lg:leading-[var(--heading-h4-line-height)] [font-style:var(--heading-h4-font-style)]">
                    {t(`bulletPoints.${index}`)}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms]">
              <Button 
                asChild
                className="h-auto inline-flex items-center justify-center gap-2.5 px-6 md:px-8 lg:px-[30px] py-3 md:py-4 lg:py-5 rounded-[5px] bg-brand-gradient text-white font-button font-[number:var(--button-font-weight)] text-sm md:text-base lg:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] hover:opacity-90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Link href="/about">
                  {t('ctaButton.text')}
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};
